import React, { useEffect } from 'react';
import {
  BooleanInput,
  NumberInput,
  maxValue,
  minValue,
  number,
  required,
  useTranslate,
} from 'react-admin';
import { useFormContext, useWatch } from 'react-hook-form';
import { getFieldHelperText } from '../../../utils/eventFormUtils';
import { MaskedDateTimeInput } from '../event-utils/MaskedDateTimeInput';

const URGENCY_MAX = {
  hours: 128,
  days: 90,
  tickets: 1000,
};

/**
 * Event Settings Tab Component
 * Handles event settings and status
 */
export const EventSettingsTab = ({ permissions = {}, record = {} }) => {
  const translate = useTranslate();
  const { setValue } = useFormContext();
  const isAdmin = permissions?.crole === 'admin';
  const refundEnabled = useWatch({ name: 'refundEnabled' });
  const startdate = useWatch({ name: 'startdate' });
  const daysEnabled = useWatch({ name: 'urgency.daysEnabled' });
  const hoursEnabled = useWatch({ name: 'urgency.hoursEnabled' });
  const ticketsEnabled = useWatch({ name: 'urgency.ticketsEnabled' });

  useEffect(() => {
    if (record && Object.keys(record).length === 0) {
      setValue('isActive', isAdmin);
      setValue('isVerified', isAdmin);
      setValue('refundEnabled', true);
      setValue('refundDeadlineAt', null);
    }
  }, [isAdmin, record, setValue]);

  useEffect(() => {
    if (refundEnabled === false) {
      setValue('refundDeadlineAt', null, { shouldDirty: true });
    }
  }, [refundEnabled, setValue]);

  return (
    <React.Fragment>
      {isAdmin && (
        <>
          <BooleanInput
            source="isActive"
            label={translate('form.show_event_on_website', 'Show this event on the website?')}
            helperText={translate('form.is_active_hint', "Please note that the event should be 'saved' for this change to take effect.")}
          />
          <BooleanInput
            source="isVerified"
            label={translate('form.verified_event', 'Verified Event?')}
            helperText={translate('form.is_verified_hint', 'Is event verified by the admin?')}
          />
        </>
      )}

      <h3 style={{ marginTop: isAdmin ? 24 : 0 }}>{translate('form.homepage_visibility', 'Anasayfa Görünürlüğü')}</h3>
      <BooleanInput
        source="hideFromWebHome"
        label={translate('form.hide_from_web_home', 'Web anasayfasında gizle')}
        helperText={translate('form.hide_from_web_home_hint', "Açıksa bu etkinlik web anasayfasındaki home section'larda gösterilmez.")}
        defaultValue={false}
      />
      <BooleanInput
        source="hideFromMobileHome"
        label={translate('form.hide_from_mobile_home', 'Mobil anasayfasında gizle')}
        helperText={translate('form.hide_from_mobile_home_hint', "Açıksa bu etkinlik mobil anasayfasındaki home section'larda gösterilmez.")}
        defaultValue={false}
      />

      <h3 style={{ marginTop: 24 }}>{translate('form.auto_urgency_badges', 'Otomatik Aciliyet Etiketleri')}</h3>
      <p style={{ fontSize: 12, color: '#666', marginTop: 0, marginBottom: 12 }}>
        {translate('form.urgency_hint', 'Kartta tek kırmızı etiket çıkar. Koşullar tutarsa öncelik: saat → bilet → gün. Örnek: son 20 saatteyse saat; değil ama az bilet kaldıysa bilet; değil ama X gün kaldıysa gün. Etiket kalan gerçek değeri yazar (ör. Son 7 bilet).')}
      </p>

      <BooleanInput
        source="urgency.hoursEnabled"
        label={translate('form.urgency_hours_label', 'Son X saat kala etiket göster')}
        defaultValue={false}
      />
      {hoursEnabled ? (
        <NumberInput
          source="urgency.hoursThreshold"
          label={translate('form.urgency_hours_threshold', 'X saat (eşik)')}
          min={1}
          max={URGENCY_MAX.hours}
          defaultValue={24}
          parse={(v) =>
            v === '' || v == null ? null : Number(v)
          }
          validate={[
            required(translate('form.hours_threshold_required', 'Saat eşiği zorunludur.')),
            number(translate('form.valid_number_required', 'Geçerli bir sayı girin.')),
            minValue(1, translate('form.hours_threshold_min', 'Saat eşiği en az 1 olmalıdır.')),
            maxValue(
              URGENCY_MAX.hours,
              translate('form.hours_threshold_max', `Saat eşiği en fazla ${URGENCY_MAX.hours} olabilir.`, { max: URGENCY_MAX.hours }),
            ),
          ]}
          fullWidth
          helperText={translate('form.hours_threshold_hint', `Etkinliğe bu kadar saat veya daha az kaldığında 'Son N saat' (1–${URGENCY_MAX.hours}).`, { max: URGENCY_MAX.hours })}
        />
      ) : null}

      <BooleanInput
        source="urgency.ticketsEnabled"
        label={translate('form.urgency_tickets_label', 'Son X bilet kala etiket göster')}
        defaultValue={false}
      />
      {ticketsEnabled ? (
        <NumberInput
          source="urgency.ticketsThreshold"
          label={translate('form.urgency_tickets_threshold', 'X bilet (eşik)')}
          min={1}
          max={URGENCY_MAX.tickets}
          defaultValue={10}
          parse={(v) =>
            v === '' || v == null ? null : Number(v)
          }
          validate={[
            required(translate('form.tickets_threshold_required', 'Bilet eşiği zorunludur.')),
            number(translate('form.valid_number_required', 'Geçerli bir sayı girin.')),
            minValue(1, translate('form.tickets_threshold_min', 'Bilet eşiği en az 1 olmalıdır.')),
            maxValue(
              URGENCY_MAX.tickets,
              translate('form.tickets_threshold_max', `Bilet eşiği en fazla ${URGENCY_MAX.tickets} olabilir.`, { max: URGENCY_MAX.tickets }),
            ),
          ]}
          fullWidth
          helperText={translate('form.tickets_threshold_hint', `Kalan bilet (koltuklu dahil) bu sayıya eşit/küçükse 'Son N bilet' (1–${URGENCY_MAX.tickets}). Saat penceresi dışındayken geçerlidir.`, { max: URGENCY_MAX.tickets })}
        />
      ) : null}

      <BooleanInput
        source="urgency.daysEnabled"
        label={translate('form.urgency_days_label', 'Son X gün kala etiket göster')}
        defaultValue={false}
      />
      {daysEnabled ? (
        <NumberInput
          source="urgency.daysThreshold"
          label={translate('form.urgency_days_threshold', 'X gün (eşik)')}
          min={1}
          max={URGENCY_MAX.days}
          defaultValue={3}
          parse={(v) =>
            v === '' || v == null ? null : Number(v)
          }
          validate={[
            required(translate('form.days_threshold_required', 'Gün eşiği zorunludur.')),
            number(translate('form.valid_number_required', 'Geçerli bir sayı girin.')),
            minValue(1, translate('form.days_threshold_min', 'Gün eşiği en az 1 olmalıdır.')),
            maxValue(
              URGENCY_MAX.days,
              translate('form.days_threshold_max', `Gün eşiği en fazla ${URGENCY_MAX.days} olabilir.`, { max: URGENCY_MAX.days }),
            ),
          ]}
          fullWidth
          helperText={translate('form.days_threshold_hint', `Kalan gün bu değere eşit/küçükse 'Son N gün' (1–${URGENCY_MAX.days}). Saat veya bilet etiketi yoksa gösterilir.`, { max: URGENCY_MAX.days })}
        />
      ) : null}

      <h3 style={{ marginTop: 24 }}>{translate('form.refund_settings', 'İade Ayarları')}</h3>
      <BooleanInput
        source="refundEnabled"
        label={translate('form.refund_enabled', 'İade kabul edilsin mi?')}
        helperText={translate('form.refund_enabled_hint', 'Kapalıysa kullanıcılar bu etkinlik için web üzerinden iade yapamaz.')}
        defaultValue={true}
      />
      {refundEnabled !== false && (
        <MaskedDateTimeInput
          source="refundDeadlineAt"
          label={translate('form.refund_deadline', 'İade son kabul tarih / saat')}
          disabled={!startdate}
          validate={[
            (value) => {
              if (!value || !startdate) return undefined;
              return new Date(value).getTime() > new Date(startdate).getTime()
                ? translate('form.refund_deadline_error', 'İade son tarihi etkinlik başlangıcından sonra olamaz.')
                : undefined;
            },
          ]}
          helperText={
            startdate
              ? translate('form.refund_deadline_hint', 'Kullanıcılar bu tarih ve saate kadar iade yapabilir. Boş bırakılırsa süre sınırı yoktur.')
              : translate('form.refund_deadline_startdate_hint', 'Önce event tarihini girin. Sonra iade için son kabul tarih/saat seçebilirsiniz.')
          }
        />
      )}
    </React.Fragment>
  );
};
